import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "pm_pwa_dismissed_at";
const DISMISS_DAYS = 14; // hide for 14 days after dismissal

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isRecentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallPWAPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || isRecentlyDismissed()) return;
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    const installedHandler = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-4 right-4 bottom-20 md:bottom-6 md:left-auto md:right-6 md:max-w-sm z-50 rounded-2xl bg-white shadow-2xl border border-slate-200 p-4 flex items-start gap-3 fade-in-up"
      data-testid="install-pwa-prompt"
    >
      <div className="h-11 w-11 rounded-2xl grid place-items-center text-white shrink-0 shadow-md shadow-indigo-200"
           style={{ background: "linear-gradient(135deg,#4F46E5,#7C3AED)" }}>
        <Smartphone className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm text-slate-900">Install PrepMasters</div>
        <p className="text-xs text-slate-600 mt-0.5">Add to your home screen for a faster, app-like experience.</p>
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" onClick={install} className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-9" data-testid="pwa-install-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Install
          </Button>
          <Button size="sm" variant="ghost" onClick={dismiss} className="h-9 text-slate-500" data-testid="pwa-dismiss-btn">
            Not now
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="h-7 w-7 -m-1 grid place-items-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
